import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  IGroupPerformanceSummary,
  useGroupPerformanceSummary,
} from 'core/hooks';

import { IOverviewWidgetProps } from './types';
import { OverviewWidgetShell } from './OverviewWidget';
import { Metric, MetricLabel, MetricValue } from './overview.styled';

/**
 * Mark formatter — identical to `PerformanceCard.fmtMark`: rounds to two
 * decimals and renders an em dash for a missing average so the widget reuses
 * the card's figures rather than re-deriving them (Requirement 1.2).
 */
const fmtMark = (n: number | null | undefined): string =>
  n == null ? '—' : (Math.round((Number(n) || 0) * 100) / 100).toString();

/**
 * OverviewPerformanceWidget (Requirement 1) — the performance health card on the
 * reworked Overview tab.
 *
 * Fed by `useGroupPerformanceSummary(groupId)` and reuses the `PerformanceCard`
 * figures (Requirement 1.2): the average mark and the count of students without
 * marks. Clicking (or pressing Enter/Space) navigates to the Schedule tab where
 * the full performance breakdown lives (Requirement 2.4).
 */
export const OverviewPerformanceWidget = memo(
  ({ groupId, onNavigate }: IOverviewWidgetProps) => {
    const { t } = useTranslation();
    const { data, loading } = useGroupPerformanceSummary(groupId);

    return (
      <OverviewWidgetShell<IGroupPerformanceSummary>
        title={t('pages.groupDetail.performance.cardTitle')}
        ariaLabel={t('pages.groupDetail.overview.openPerformance')}
        state={{ data, loading, isEmpty: !data }}
        onActivate={() => onNavigate('schedule')}
      >
        {(summary) => {
          const avg = summary.averageMark;
          const withoutMarks = summary.studentsWithoutMarks ?? 0;

          return (
            <>
              <Metric>
                <MetricLabel>
                  {t('pages.groupDetail.performance.averageMark')}
                </MetricLabel>
                <MetricValue tone={(avg ?? 0) >= 4 ? 'success' : undefined}>
                  {fmtMark(avg)}
                </MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>
                  {t('pages.groupDetail.performance.studentsWithoutMarks')}
                </MetricLabel>
                <MetricValue tone={withoutMarks > 0 ? 'warn' : undefined}>
                  {withoutMarks}
                </MetricValue>
              </Metric>
            </>
          );
        }}
      </OverviewWidgetShell>
    );
  },
);
